// src/examples/Tables/ExtraCollapse/index.js

import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useSortBy, useExpanded } from "react-table";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material-UI components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Autocomplete from "@mui/material/Autocomplete";
import Pagination from "@mui/material/Pagination";
import IconButton from "@mui/material/IconButton";
import { Box, Input } from "@mui/material";

// Material-UI icons
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { LuArrowUpDown, LuArrowDown, LuArrowUp } from "react-icons/lu";

// Custom components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import colors from "assets/theme/base/colors";
import { SEARCH_INPUT_TYPES } from "helpers/constant";

const { sideNav } = colors;

function ExtraCollapse({
  table,
  tableTitle,
  renderSubRow,
  showEntriesPerPage = true,
  showTotalEntries = true,
  canFilter = false,
  rowCount = 0,
  defaultFilter,
  setDefaultFilter,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows, // use rows instead of page
    visibleColumns,
  } = tableInstance;

  const { currentPage, itemsPerPage } = defaultFilter || {};

  const handleEntriesChange = (event, newValue) => {
    const newPageSize = parseInt(newValue, 10);
    setDefaultFilter((value) => ({
      ...value,
      itemsPerPage: newPageSize,
      currentPage: 1, // Reset to first page
    }));
  };

  const handlePageChange = (event, newPage) => {
    if (newPage !== currentPage) {
      setDefaultFilter((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleSortChange = (colId) => {
    setDefaultFilter((prevState) => {
      const existingSortBy = prevState.sortBy.find((sort) => sort.id === colId);
      let updatedSortBy;
      if (!existingSortBy) {
        updatedSortBy = [{ id: colId, value: "asc" }];
      } else if (existingSortBy.value === "asc") {
        updatedSortBy = [{ id: colId, value: "desc" }];
      } else {
        updatedSortBy = [];
      }
      return { ...prevState, sortBy: updatedSortBy };
    });
  };

  const entriesStart = (currentPage - 1) * itemsPerPage + 1;
  const entriesEnd = Math.min(currentPage * itemsPerPage, rowCount);

  return (
    <TableContainer sx={{ boxShadow: sideNav.boxShadowTwo, borderRadius: "0.75rem" }}>
      {/* HEADER AND FILTERS */}
      <MDBox pt={3} px={2} display="flex" justifyContent="space-between" alignItems="center">
        {tableTitle && (
          <MDTypography variant="h6" fontWeight="medium">
            {tableTitle}
          </MDTypography>
        )}
      </MDBox>
      {showEntriesPerPage && (
        <MDBox p={2}>
          <MDBox display="flex" alignItems="center">
            <Autocomplete
              disableClearable
              value={defaultFilter?.itemsPerPage.toString()}
              options={["5", "10", "15", "20", "25", "50", "100"]}
              onChange={handleEntriesChange}
              size="small"
              sx={{ width: "5rem" }}
              renderInput={(params) => <MDInput {...params} />}
            />
            <MDTypography variant="caption" color="secondary" sx={{ ml: 1, fontWeight: "medium" }}>
              Entries per page
            </MDTypography>
          </MDBox>
        </MDBox>
      )}

      <Table {...getTableProps()}>
        {/* THEAD SECTION */}
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <DataTableHeadCell
                  {...column.getHeaderProps(column.sort && column.getSortByToggleProps())}
                >
                  <MDBox
                    display="flex"
                    fontSize={12}
                    style={{ color: "#6c757d" }}
                    gap="10px"
                    alignItems="center"
                  >
                    {column.render("Header")}
                    {column.sort && (
                      <MDBox sx={{ cursor: "pointer" }} onClick={() => handleSortChange(column.id)}>
                        {defaultFilter.sortBy.find((item) => item.id === column.id)?.value ===
                        "asc" ? (
                          <LuArrowUp />
                        ) : defaultFilter.sortBy.find((item) => item.id === column.id)?.value ===
                          "desc" ? (
                          <LuArrowDown />
                        ) : (
                          <LuArrowUpDown />
                        )}
                      </MDBox>
                    )}
                  </MDBox>
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>

        {/* FILTER ROW SECTION */}
        {canFilter && (
          <TableBody>
            <TableRow>
              {columns.map((column) => (
                <DataTableBodyCell key={column.id}>
                  {column?.filterInputType === SEARCH_INPUT_TYPES.AUTO_COMPLETE && (
                    <MDBox display="flex" alignItems="center" sx={{ minWidth: "180px" }}>
                      <Autocomplete
                        options={column?.filterOption}
                        onChange={(event, newValue) => {
                          column?.filterHandleChange(column?.filterAccessor, newValue || "");
                        }}
                        size="small"
                        sx={{
                          width: "85% !important",
                          color: "#BFC3DA",
                          padding: "0px",
                          border: "0",
                        }}
                        popupIcon={
                          <KeyboardArrowDownIcon sx={{ fontSize: "14px", width: "18px" }} />
                        }
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            placeholder={column?.filterLabel}
                            variant="standard"
                            sx={{ fontWeight: "400" }}
                          />
                        )}
                      />
                    </MDBox>
                  )}
                  {column.filterInputType === SEARCH_INPUT_TYPES.TEXT ? (
                    <MDBox display="flex" alignItems="center" sx={{ minWidth: "180px" }}>
                      <Input
                        sx={{
                          fontSize: "12px",
                          padding: "10px 0px 0px 5px",
                        }}
                        type="search"
                        value={column?.filterValue}
                        onChange={(e) => {
                          column?.filterHandleChange(column?.filterAccessor, e.target.value);
                        }}
                        placeholder={column?.filterLabel}
                        inputProps={{ "aria-label": "description" }}
                      />
                    </MDBox>
                  ) : null}
                </DataTableBodyCell>
              ))}
            </TableRow>
          </TableBody>
        )}

        {/* TABLE BODY SECTION */}
        <TableBody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <React.Fragment key={row.id}>
                {/* Main Row */}
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell, i) => (
                    <DataTableBodyCell {...cell.getCellProps()}>
                      {/* Add expander icon to the first cell */}
                      {i === 0 ? (
                        <MDBox display="flex" alignItems="center" sx={{ gap: 0.5 }}>
                          <IconButton size="small" {...row.getToggleRowExpandedProps()}>
                            {row.isExpanded ? (
                              <KeyboardArrowDownIcon />
                            ) : (
                              <KeyboardArrowRightIcon />
                            )}
                          </IconButton>
                          {cell.render("Cell")}
                        </MDBox>
                      ) : (
                        cell.render("Cell")
                      )}
                    </DataTableBodyCell>
                  ))}
                </TableRow>

                {/* Expanded Sub-Row */}
                {row.isExpanded && (
                  <TableRow>
                    <td colSpan={visibleColumns.length} style={{ padding: 0, border: "none" }}>
                      {renderSubRow({ row })}
                    </td>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      {/* FOOTER AND PAGINATION */}
      {rows?.length === 0 && (
        <MDBox
          color={sideNav.textColor}
          fontWeight="500"
          fontSize="20px"
          style={{
            width: "100%",
            display: "block",
            textAlign: "center",
            paddingTop: "24px",
          }}
        >
          No records found!
        </MDBox>
      )}

      {showTotalEntries && (
        <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rowCount} entries
            </MDTypography>
          </MDBox>
          {rowCount > itemsPerPage && (
            <Pagination
              count={Math.ceil(rowCount / itemsPerPage)}
              color="info"
              page={currentPage}
              onChange={handlePageChange}
              hidePrevButton={currentPage === 1}
              hideNextButton={currentPage === Math.ceil(rowCount / itemsPerPage)}
              sx={{
                position: "absolute",
                right: "40px",
                "& .MuiPaginationItem-root": {
                  height: "36px",
                  width: "36px",
                  fontSize: "16px",
                  borderRadius: "50%",
                  border: "1px solid #7b809a",
                  "&.Mui-selected": {
                    background: `${colors.info.main} !important`,
                    color: "#FFFFFF",
                    cursor: "default",
                  },
                },
              }}
            />
          )}
        </MDBox>
      )}
    </TableContainer>
  );
}

// PropTypes and DefaultProps
ExtraCollapse.defaultProps = {
  tableTitle: "",
  showEntriesPerPage: true,
  showTotalEntries: true,
  canFilter: false,
};

ExtraCollapse.propTypes = {
  table: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  tableTitle: PropTypes.string,
  renderSubRow: PropTypes.func.isRequired,
  showEntriesPerPage: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  canFilter: PropTypes.bool,
  rowCount: PropTypes.number.isRequired,
  defaultFilter: PropTypes.object.isRequired,
  setDefaultFilter: PropTypes.func.isRequired,
};

export default ExtraCollapse;
